import React, { useState, useEffect } from 'react';

import Dialog from '@mui/material/Dialog';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';

import Box from '@mui/system/Box';
import { equals, lensPath, lensProp, pathOr, set, toLower } from 'ramda';
import { connect } from 'react-redux';
import { compose } from 'redux';

import ChevronLeftIcon from '../../../assets/icons/chevron-left.svg';

import Button from 'src/components/Buttons/Button';
import { Switch } from 'src/components/Switch/Switch';

import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import { Theme, useTheme } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';

import SummaryInput from './SummaryTextInput';
import { MapState } from 'src/store/types';
import { META_DATA } from 'src/services/constants';
import useNotification from 'src/features/ToastNotifications/ToastNotifications';
import { updateAccountInfo, updateJustPassword } from 'src/services/account';
import { FALSE, NO, YES } from 'src/constants';
import useAccounts from 'src/hooks/useAccountsHook';
import { PHONE_NUMBER_TEXT } from 'src/features/core/Intents/intentConstants';
import siteSetting from 'src/siteSetting';
import { capitalizeAllWords } from 'src/utilities/capitalize';


const useMenuStyles = makeStyles((theme: Theme) =>
  createStyles({
    goBackBtn: {
      top: 16,
      left: 10,
      position: 'absolute',
      justifyContent: 'flex-start',
      paddingLeft: '16px',
      [theme.breakpoints.down('sm')]: {
        paddingLeft: '8px'
      }
    },
    dialogTitle: {
      textAlign: 'center',
      fontFamily: 'Martel,serif',
    },
    dialogContent: {
      [theme.breakpoints.down('sm')]: {
        paddingLeft: '16px',
        paddingRight: '16px',
        paddingTop: 0
      },
    },
    dialogHeader: {
      borderTop: '1px solid #EEE',
    },
    wrapper: {
      display: 'flex',
      flexDirection: 'column',
      padding: 20,
      background: theme.palette.mainLight,
      border: `1px solid ${theme.palette.gray.extraLightGray}`,
      boxShadow: '0px 5px 7px rgba(0, 0, 0, 0.05)',
      borderRadius: 5,
    },
    profileMetaContainer: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: 10,
    },
  })
);

type Props = {
  openProfile: boolean;
  setOpenProfile: () => void;
};

type CombinedProps = Props & StateProps;

const ApplicantProfilePopup: React.FC<CombinedProps> = (props: CombinedProps) => {
  const classes = useMenuStyles();
  const { openProfile, setOpenProfile, accounts } = props
  const { accountFirstName, accountLastName, accountEmail, cellMobilePhone, sms_alerts, marketing_alerts } = useAccounts();


  const [inputValue, setValue] = useState({
    firstName: accountFirstName,
    lastName: accountLastName,
    email: accountEmail,
  });


  const [smsNotif, setSmsNotif] = useState<any>();
  const [marketingNotif, setMarketingNotif] = useState<any>();
  const [isEdit, setEdit] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState(cellMobilePhone)
  const [password, setPassword] = useState('');
  const [sendNotification] = useNotification();
  const [updateErrors, setUpdateErrors] = useState([]);

  useEffect(() => {
    if (accounts) {
      setValue({
        firstName: accountFirstName,
        lastName: accountLastName,
        email: accountEmail,
      })
      setPhoneNumber(cellMobilePhone);
      setSmsNotif(equals(sms_alerts, YES))
      setMarketingNotif(equals(marketing_alerts, YES))
    }
  }, [accounts]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>, shouldCapitalize = false) => {
    const { name, value } = event.target;
    setValue((prevState: any) => ({
      ...prevState,
      [name]:  shouldCapitalize ? capitalizeAllWords(toLower(value)): value 
    }));
  };

  const onSubmit = (smsAlert?: boolean, marketingAlert?: boolean) => {
    const data = compose(
      set(lensPath(['first_name']), inputValue.firstName),
      set(lensPath(['last_name']), inputValue.lastName),
      set(lensPath(['email']), inputValue.email),
      set(lensPath(['phone']), phoneNumber),
      set(lensPath([META_DATA, 'lendsmart_sh/sms_alerts']), smsAlert === true ? YES : NO),
      set(lensPath([META_DATA, 'lendsmart_sh/marketing_alerts']), marketingAlert === true ? YES : NO),
    )(accounts);

    updateAccountInfo(data).then((response) => {
      if (response) {
        sendNotification({
          msg: "Updated",
          variant: 'success',
        });
      }
    }).catch((error) => {
      sendNotification({
        msg: "Not updated.",
        variant: 'error',
      });
    });
  }

  const updatePassword = () => {
    const passwordData = compose(
      set(lensPath(['id']), pathOr('', ['id'], accounts)),
      set(lensPath(['email']), pathOr('', ['email'], accounts)),
      set(lensProp('password'), password),
    )({});

    if (password.length > 0) {
      updateJustPassword(passwordData)
        .then((response) => {
          if (response) {
            sendNotification({
              msg: "Updated",
              variant: 'success',
            });
          }
          setPassword('')
        })
        .catch((errResponse) => {
          onErrorOfUser(errResponse);
        });
    }
  }

  const handlePasswordChange = (value: any) => {
    setPassword(value);
  }

  const handlePhoneChange = (value: any) => {
    setPhoneNumber(value);
  };

  const handleChangeSmsNotif = (alert: boolean) => {
    setSmsNotif(!smsNotif);
    onSubmit(alert, marketingNotif);
  };

  const handleChangemarketingNotif = (alert: boolean) => {
    setMarketingNotif(!marketingNotif);
    onSubmit(smsNotif, alert);
  };

  const handleSave = () => {
    updatePassword()
    onSubmit(smsNotif, marketingNotif);
    setEdit(false);
  };

  const handleClosePassword = () => {
    updatePassword()
    setEdit(false);
  }

  const onErrorOfUser = (errResponse: any) => {
    const errorMessage = pathOr('', ['response', 'data', 'message'], errResponse);
    sendNotification({
      msg: errorMessage || "Not updated",
      variant: 'error',
    });
    setUpdateErrors([errorMessage] as any);
  };

  const { breakpoints } = useTheme();
  const matches = useMediaQuery(breakpoints.down('sm'));

  return (
    <Dialog open={openProfile} onClose={setOpenProfile} maxWidth="md" fullWidth>
      <Box className={classes.dialogHeader}>
        <DialogTitle className={classes.dialogTitle}>Profile</DialogTitle>

        <Button
          pr={30}
          py={6}
          text={matches ? '' : "Go back"}
          variant="text"
          onClick={setOpenProfile}
          startIcon={<ChevronLeftIcon />}
          className={classes.goBackBtn}
        />
      </Box>

      <DialogContent className={classes.dialogContent}>
        <Grid container flexDirection="column">
          <Box mb={2} className={classes.wrapper}>
            <Typography variant="h6" color="primaryColor" gutterBottom>
              Personal information
            </Typography>

            <Box className={classes.profileMetaContainer}>
              <Box
                sx={{
                  flex: 1,
                }}
              >
                <Typography
                  variant="caption"
                  component="p"
                  color="textSecondary"
                >
                  First name
                </Typography>
                <SummaryInput
                  value={inputValue.firstName}
                  onChange={(e)=> handleChange(e, true)}
                  type="text"
                  name="firstName"
                  isEdit={isEdit}
                />
              </Box>
            </Box>

            <Box className={classes.profileMetaContainer}>
              <Box
                sx={{
                  flex: 1,
                }}
              >
                <Typography
                  variant="caption"
                  component="p"
                  color="textSecondary"
                >
                  Last name
                </Typography>
                <SummaryInput
                  value={inputValue.lastName}
                  onChange={(e)=> handleChange(e, true)}
                  type="text"
                  name="lastName"
                  isEdit={isEdit}
                />
              </Box>
            </Box>

            <Box className={classes.profileMetaContainer}>
              <Box
                sx={{
                  flex: 1,
                }}
              >
                <Typography
                  variant="caption"
                  component="p"
                  color="textSecondary"
                >
                  Mobile phone number
                </Typography>


                <SummaryInput
                  value={phoneNumber}
                  onChange={handlePhoneChange}
                  type={PHONE_NUMBER_TEXT}
                  isEdit={isEdit}
                />
              </Box>
            </Box>
          </Box>

          <Box className={classes.wrapper}>
            <Typography variant="h6" color="primaryColor" gutterBottom>
              Account settings
            </Typography>

            <Box className={classes.profileMetaContainer}>
              <Box
                sx={{
                  flex: 1,
                }}
              >

                <Typography
                  variant="caption"
                  component="p"
                  color="textSecondary"
                >
                  Email
                </Typography>
                <SummaryInput
                  value={inputValue.email}
                  onChange={handleChange}
                  type="text"
                  name="email"
                  isEdit={isEdit}
                />
              </Box>
            </Box>

            <Box className={classes.profileMetaContainer}>
              <Box
                sx={{
                  flex: 1,
                }}
              >
                <Typography
                  variant="caption"
                  component="p"
                  color="textSecondary"
                >
                  Password
                </Typography>
                <SummaryInput
                  value={password}
                  onPasswordChange={handlePasswordChange}
                  type="password"
                  isEdit={isEdit}
                  updateErrors={updateErrors}
                  onClose={handleClosePassword}
                />
              </Box>
            </Box>

            <Box className={classes.profileMetaContainer}>
              <Box
                sx={{
                  flex: 1,
                }}
              >
                <Typography
                  variant="caption"
                  component="p"
                  color="textSecondary"
                >
                  Alerts
                </Typography>
                <Typography
                  variant="caption"
                  component="p"
                  fontWeight="700"
                  gutterBottom
                >
                  SMS Notifications
                </Typography>
              </Box>
              <Switch
                isChecked={smsNotif}
                handleChange={() => handleChangeSmsNotif(!smsNotif)}
              />
            </Box>
            {equals(siteSetting.generalDisableMarketingAlerts(), FALSE) &&
              <Box className={classes.profileMetaContainer}>
                <Box
                  sx={{
                    flex: 1,
                  }}
                >
                  <Typography
                    variant="caption"
                    component="p"
                    color="textSecondary"
                  >
                    Alerts
                  </Typography>
                  <Typography
                    variant="caption"
                    component="p"
                    fontWeight="700"
                    gutterBottom
                  >
                    Marketing updates
                  </Typography>
                </Box>
                <Switch
                  isChecked={marketingNotif}
                  handleChange={() => handleChangemarketingNotif(!marketingNotif)}
                />
              </Box>}
          </Box>
          <Box m={2}>
            <Grid container display="flex" justifyContent="flex-end" textAlign="center" gap={5}>
              {isEdit ? (
                <>
                  <Button text="Save" onClick={() => handleSave()} />
                  <Button text="Cancel" onClick={() => setEdit(false)} />
                </>
              ) : <Button text="Edit" onClick={() => setEdit(true)} />}
            </Grid>
          </Box>
        </Grid>
      </DialogContent>
    </Dialog>
  );
}

interface StateProps {
  accounts: any;
}

const mapStateToProps: MapState<StateProps, Props> = (state) => ({
  accounts: pathOr({}, ['accounts', 'data'], state),
});

export const connected = connect(mapStateToProps)

export default compose(connected)(ApplicantProfilePopup);
